export const en = {
	auth: {
		login: {
			headline: "Tell your story.",
			subheadline: "Sign in to continue where you left off.",
			emailPlaceholder: "your@email.com",
			passwordPlaceholder: "password",
			submitIdle: "Sign in",
			submitLoading: "Signing in...",
			noAccount: "No account yet?",
			beginHere: "Begin here.",
			errorInvalid: "Invalid email or password.",
			errorGeneric: "Something went wrong. Please try again.",
		},
		register: {
			headline: "Begin here.",
			subheadline: "Every story starts somewhere.",
			namePlaceholder: "your name",
			emailPlaceholder: "your@email.com",
			passwordPlaceholder: "choose a password",
			submitIdle: "Create account",
			submitLoading: "Creating account...",
			alreadyHaveAccount: "Already have an account?",
			signIn: "Sign in.",
			errorConflict: "An account with that email already exists.",
			errorGeneric: "Something went wrong. Please try again.",
		},
	},
	stories: {
		list: {
			headline: "Your stories.",
			beginNew: "Begin a new story.",
			loading: "Loading your stories...",
			errorLoad: "Could not load stories. Try refreshing.",
			empty: "You haven't written anything yet.",
			noTag: 'No stories tagged "{{tag}}".',
			filterBy: "Filter by tag",
			clearFilter: "Clear filter",
			showAll: "Show all stories",
			storiesCount_one: "{{count}} story",
			storiesCount_other: "{{count}} stories",
			filteredCount: "Showing {{filtered}} of {{total}}",
			searchPlaceholder: "Search your stories…",
			sortNewest: "Newest first",
			sortOldest: "Oldest first",
			pageOf: "Page {{page}} of {{pages}}",
			noResults: "No stories match your search.",
		},
		new: {
			back: "← Back",
			titlePlaceholder: "Give it a name, or leave it blank.",
			voicePrompt:
				"Tell your story as if you were talking to a friend. Messy is fine.",
			switchToText: "Write instead →",
			writePrompt: "Write it like you'd tell it to a friend. Messy is fine.",
			switchToVoice: "← Use voice instead",
			modeText: "Write",
			modeVoice: "Speak",
			submitIdle: "Send it off.",
			submitLoading: "Sending...",
			errorGeneric: "Something went wrong. Please try again.",
		},
		card: {
			untitled: "Untitled Story",
			status: {
				processing: "Processing...",
				awaiting_response: "Awaiting your response",
				done: "Reviewed",
				failed: "Failed",
			},
		},
		detail: {
			loading: "Loading...",
			notFound: "Story not found.",
			processing: {
				reading: "Listening…",
				transcribing: "Making sense of it…",
			},
			failed: {
				heading: "Something went wrong",
				explanation:
					"Something went wrong while processing your story. You can try again — your original recording is safe.",
				tryAgain: "Try again",
				retrying: "Retrying…",
			},
			coaching: {
				akinNotes: "Akin's notes",
				showNote: "Show {{label}}",
				questionStep: "Question {{step}} of {{total}}",
				takeYourTime: "Take your time...",
				switchToVoice: "← Use voice instead",
				switchToText: "Write instead →",
				back: "← Back",
				next: "Next",
				skip: "Skip",
				doneSendAnswers: "Done — send answers",
				reviewAndSubmit: "Review & submit",
				uploading: "Uploading…",
				imDone: "I'm done with this story.",
				imDoneConfirm: "This will mark your story as finished. Are you sure?",
				imDoneYes: "Yes, mark as done",
				imDoneCancel: "Keep going",
				transcribing_one: "Transcribing {{count}} answer…",
				transcribing_other: "Transcribing {{count}} answers…",
				transcriptionFailed: "Transcription failed — type your answer",
				transcriptionFailedNote:
					"Transcription failed — type your answer or leave blank.",
				leaveBlank: "Leave blank to skip",
				submit: "Submit",
				submitting: "Sending…",
				voiceUploadFailed:
					"Some voice answers couldn't be uploaded. Type them in below.",
				retryVoiceUpload: "Try uploading again",
			},
			transcriptionPreview: {
				heading: "Here's what we heard",
				noTranscription: "No transcription available.",
				editTranscription: "Edit transcription",
				cancel: "Cancel",
				save: "Save",
				saving: "Saving…",
				looksGood: "Looks good — review it",
				starting: "Starting…",
			},
			storyUpdate: {
				roundComplete: "Round {{number}} complete",
				akinNotes: "Akin's notes",
				titlePlaceholder: "Give it a name, or leave it blank.",
				storyPlaceholder: "Your story will appear here...",
				saveChanges: "Save changes",
				saving: "Saving…",
				keepGoing: "Keep going",
				imDone: "I'm done with this story.",
			},
			done: {
				akinNotes: "Akin's notes",
				backToStories: "← Your stories",
				yourRecording: "Your recording",
				copyText: "Copy text",
				copied: "Copied.",
				answerQuestionsAgain: "Answer questions again",
				starting: "Starting…",
				beginNew: "Begin a new story",
				edit: "Edit",
				cancel: "Cancel",
				saveChanges: "Save changes",
				saving: "Saving…",
				recordStory: "Record your story",
				deleteStory: "Delete story",
			},
			deleteConfirm: {
				heading: "Delete this story?",
				body: "This cannot be undone. The story and all its history will be permanently removed.",
				confirm: "Yes, delete",
				cancel: "Cancel",
				deleting: "Deleting…",
			},
			rerecord: {
				eyebrow: "One more thing",
				heading: "Tell it again, now that you remember more.",
				body: "You've done the hard work of remembering. This is your chance to record the story the way it actually happened — in your own voice, with all the detail you've uncovered.",
				modeReadTitle: "Read it aloud",
				modeReadDesc:
					"We'll show you the refined story to read at your own pace. Good if you want it to feel polished.",
				modeRetellTitle: "Tell it in your own words",
				modeRetellDesc:
					"No script. Just you and what you remember now. Good if you want it to feel like you.",
				startRecording: "Start recording",
				saveRecording: "Save this recording",
				saving: "Saving…",
				skipNow: "Not now — maybe later",
				teleprompterLabel: "Your story",
				error: "Something went wrong. Please try again.",
				uploadFailed: "Your recording couldn't be saved.",
				retryUpload: "Try uploading again",
			},
			sourceMaterial: {
				trigger: "Source material",
				initialStory: "Initial story",
				close: "Close",
				originalRecording: "Original recording",
				originalTranscript: "Original transcript",
				noTranscript: "No transcript available.",
				noRecording: "This story was written, not recorded.",
				qaRound: "Round {{number}}",
				skipped: "—",
			},
		},
	},
	profile: {
		headline: "Your profile.",
		back: "← Back",
		email: "Email",
		name: "Name",
		edit: "Edit",
		save: "Save",
		saving: "Saving…",
		cancel: "Cancel",
		changePassword: "Change password",
		currentPassword: "Current password",
		newPassword: "New password",
		passwordUpdated: "Password updated.",
		signOut: "Sign out",
		memberSince: "Member since {{date}}",
		storiesCount_one: "{{count}} story written",
		storiesCount_other: "{{count}} stories written",
		errorPasswordWrong: "Current password is incorrect.",
		errorGeneric: "Something went wrong. Please try again.",
	},
	audio: {
		startRecording: "Start recording",
		stopRecording: "Stop recording",
		tapToRecord: "Tap to record",
		tapToStop: "Tap to stop",
		reRecord: "Re-record",
		ready: "Ready ✓",
		micDenied:
			"Microphone access denied. Please allow it in your browser settings.",
	},
	tags: {
		ariaLabel: "Story tags",
		removeTag: "Remove tag {{tag}}",
		placeholder: "tag name…",
		addTag: "+ tag",
	},
	onboarding: {
		intro: {
			skip: "Skip",
			alreadyHaveAccount: "Already have an account?",
			logIn: "Log in.",
			screens: [
				{
					headline: "Every life is a library.",
					body: "Not the curated version. The real one — the smells, the mistakes, the meals, the moments nobody else remembers.",
					cta: "Next →",
				},
				{
					headline: "Akin helps you tell it.",
					body: "You speak, write, or just remember. Akin listens, asks the right questions, and shapes what you say into something worth keeping.",
					cta: "Next →",
				},
				{
					headline: "Your first story starts now.",
					body: "It doesn't have to be perfect. It just has to be true.",
					cta: "Begin →",
				},
			],
		},
		prompts: {
			eyebrow: "Your first story",
			headline: "What do you want to remember?",
			subtext: "Pick one that feels right.",
			note: "You can explore more prompts anytime. For now, pick one that feels right.",
			cta: "Begin recording →",
		},
		celebration: {
			eyebrow: "Your first story",
			headline: "It's saved.",
			body: "Your first story is saved. Keep going whenever you're ready.",
			cta: "See your archive →",
		},
	},
	home: {
		welcome: "Welcome, {{name}}.",
		beginNewStory: "Begin a new story",
		loading: "Loading…",
		error: "Something went wrong. Please refresh.",
		todaysPrompt: "Today's Prompt",
		recentStories: "Recent Stories",
		untitled: "Untitled",
		stillTellingMany: "{{count}} stories you're still telling",
		continue: "Continue",
		record: "Record",
		write: "Write",
		anotherPrompt: "Another prompt",
		startWithoutPrompt: "Start without a prompt",
		storiesEmpty: "Your stories will live here.",
		viewAll: "View all stories",
		totalStories_one: "{{count}} story →",
		totalStories_other: "{{count}} stories →",
		encouragement: {
			line0: "You're building something real.",
			line1: "Three this week. Keep going.",
			line2: "These stories are yours forever.",
			line3: "A life worth remembering, story by story.",
			line4: "Every word here matters.",
			withCount: "{{count}} stories this week. {{line}}",
		},
	},
} as const;
